import { Accordion, Divider, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import RerunMenu from '@/components/RerunMenu.tsx';
import { buildRetryAttemptItems } from '@/pages/Run/components/RetryAttemptItems.ts';
import useRetryPresentation from '@/hooks/api/stepExecutions/useRetryPresentation.ts';
import ChildScopeAccordionItem from '@/pages/Run/components/ChildScopeAccordionItem.tsx';
import { childResultsScrollAreaStyles } from '@/pages/Run/components/childResultsScrollAreaStyles.ts';
import RunInlineEmptyState from '@/pages/Run/components/RunInlineEmptyState.tsx';
import {
  buildChildScopeItems,
  type ChildScopeChoice,
} from '@/pages/Run/components/ScopeStack/childScopeModel.ts';
import { Run } from '@/types/runs.ts';
import {
  ParentStepRun,
  RetryPresentation,
  StepExecution,
  getStepExecutionChildScopeState,
} from '@/types/stepExecutions.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import { StepType } from '@/types/automationFields.ts';
import { StepRun } from '@/types/stepExecutions.ts';
import { AutoExpandPlan } from '@/types/autoExpandPlan.ts';

type props = {
  run: Run;
  stepRun: StepRun;
  stepExecution: StepExecution;
  parentSteps: ParentStepRun[];
  stepsById: Record<string, AutomationStep>;
  autoExpandPlan?: AutoExpandPlan | null;
};

const rerunMenuStepTypes = [StepType.Iterate, StepType.Repeat, StepType.Retry];
const inlineChildResultStepTypes = [
  StepType.Conditional,
  StepType.FailureHandler,
  StepType.LocalSubflowCall,
];

const StepIterationsRenderer = ({
  run,
  stepRun,
  stepExecution,
  parentSteps,
  stepsById,
  autoExpandPlan,
}: props) => {
  const [showChildren, setShowChildren] = useState(false);
  const childScopeState = getStepExecutionChildScopeState(stepExecution);
  const childScopeItems = childScopeState
    ? buildChildScopeItems(childScopeState, parentSteps, stepsById)
    : [];
  const isRetryStep = stepRun.step_type === StepType.Retry;
  const rendersChildResultsInline = inlineChildResultStepTypes.includes(stepRun.step_type);
  const {
    data: retryPresentation,
    isPending: isRetryPresentationPending,
    isError: isRetryPresentationError,
  } = useRetryPresentation(stepRun.id, isRetryStep && showChildren);
  const { items: retryItems, latestItemValue } = buildRetryAttemptItems({
    presentation: retryPresentation,
  });
  const hasRetryItems = retryItems.length > 0;
  const hasChildScopeItems = childScopeItems.length > 0;
  const panelHasItems = isRetryStep ? hasRetryItems : hasChildScopeItems;
  const childResultCount = isRetryStep ? retryItems.length : childScopeItems.length;
  const targetScope = autoExpandPlan?.child_scopes_to_open.find(
    (scope) => scope.parent_step_run_id === stepRun.id,
  );
  const shouldOpenChildren =
    !!targetScope ||
    (autoExpandPlan?.common_parent_step_run_id === stepRun.id &&
      autoExpandPlan.open_common_parent_children);

  useEffect(() => {
    if (!rendersChildResultsInline && shouldOpenChildren) setShowChildren(true);
  }, [autoExpandPlan, rendersChildResultsInline, shouldOpenChildren]);

  const childResults = (
    <ChildResultsContent
      run={run}
      parentSteps={parentSteps}
      items={isRetryStep ? retryItems : childScopeItems}
      stepsById={stepsById}
      isRetryStep={isRetryStep}
      retryPresentation={retryPresentation}
      trackedLatestItemValue={isRetryStep ? latestItemValue : null}
      autoOpenChildIndex={targetScope?.child_index}
      autoOpenAttemptNumber={targetScope?.attempt_number}
      autoExpandPlan={autoExpandPlan}
    />
  );

  if (rendersChildResultsInline) {
    // A conditional can select an action that does not execute child steps.
    // In that case, there is no child-results UI to show.
    return hasChildScopeItems ? (
      <>
        <Divider />
        {childResults}
      </>
    ) : null;
  }

  return (
    <>
      <Divider />
      <Accordion
        mt={'-4px'}
        mb={'-4px'}
        value={showChildren ? 'children' : null}
        onChange={(value) => setShowChildren(value === 'children')}
      >
        <Accordion.Item value={'children'} style={{ borderBottom: '0px' }}>
          <Accordion.Control>
            <Group wrap={'nowrap'}>
              <Text size="sm">
                <b>{`${getChildResultsLabel(stepRun.step_type)}${childResultCount ? ` (${childResultCount})` : ''}`}</b>
              </Text>
              {rerunMenuStepTypes.includes(stepRun.step_type) && (
                <Group ml={'auto'} wrap={'nowrap'} gap={'4px'}>
                  <RerunMenu
                    run={run}
                    stepRun={stepRun}
                    step={stepsById[stepRun.step_id]}
                    rerunAllData={{ childrenToRerun: 'all' }}
                    rerunFailedData={
                      stepRun.step_type === StepType.Iterate ? { childrenToRerun: 'failed' } : null
                    }
                  />
                </Group>
              )}
            </Group>
          </Accordion.Control>
          <Accordion.Panel styles={{ content: { padding: 0 } }}>
            {panelHasItems ? (
              childResults
            ) : isRetryStep && isRetryPresentationPending ? (
              <RunInlineEmptyState message="Loading retry attempts..." />
            ) : isRetryStep && isRetryPresentationError ? (
              <RunInlineEmptyState message="Unable to load retry attempts." />
            ) : isRetryStep ? (
              <RunInlineEmptyState message="No retry attempts recorded." />
            ) : (
              <RunInlineEmptyState message="No child results" />
            )}
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </>
  );
};

type ChildResultsContentProps = {
  run: Run;
  parentSteps: ParentStepRun[];
  items: StepIterationItem[];
  stepsById: Record<string, AutomationStep>;
  isRetryStep: boolean;
  retryPresentation?: RetryPresentation;
  trackedLatestItemValue?: string | null;
  autoOpenChildIndex?: number;
  autoOpenAttemptNumber?: number | null;
  autoExpandPlan?: AutoExpandPlan | null;
};

const ChildResultsContent = ({
  run,
  parentSteps,
  items,
  stepsById,
  isRetryStep,
  retryPresentation,
  trackedLatestItemValue,
  autoOpenChildIndex,
  autoOpenAttemptNumber,
  autoExpandPlan,
}: ChildResultsContentProps) => (
  <Stack gap={4}>
    {isRetryStep && retryPresentation && (
      <Stack gap={2} px={4} pt={4}>
        <Text size="xs" c="dimmed">{retryPresentation.headline}</Text>
        {retryPresentation.detail && <Text size="xs" c="dimmed">{retryPresentation.detail}</Text>}
      </Stack>
    )}
    <ScrollArea.Autosize
      mah={parentSteps.length === 1 ? 400 : Infinity}
      h={'100%'}
      styles={childResultsScrollAreaStyles}
    >
      {isRetryStep ? (
        <StepIterationsRendererInner
          run={run}
          parentSteps={parentSteps}
          items={items}
          stepsById={stepsById}
          trackedLatestItemValue={trackedLatestItemValue}
          autoOpenChildIndex={autoOpenChildIndex}
          autoOpenAttemptNumber={autoOpenAttemptNumber}
          autoExpandPlan={autoExpandPlan}
        />
      ) : (
        <StepIterationsRendererInner
          run={run}
          parentSteps={parentSteps}
          items={items}
          stepsById={stepsById}
          trackedLatestItemValue={trackedLatestItemValue}
          autoOpenChildIndex={autoOpenChildIndex}
          autoOpenAttemptNumber={autoOpenAttemptNumber}
          autoExpandPlan={autoExpandPlan}
        />
      )}
    </ScrollArea.Autosize>
  </Stack>
);

type innerProps = {
  run: Run;
  parentSteps: ParentStepRun[];
  items: StepIterationItem[];
  stepsById: Record<string, AutomationStep>;
  trackedLatestItemValue?: string | null;
  autoOpenChildIndex?: number;
  autoOpenAttemptNumber?: number | null;
  autoExpandPlan?: AutoExpandPlan | null;
};

type StepIterationItem = ChildScopeChoice;

const StepIterationsRendererInner = ({
  run,
  parentSteps,
  items,
  stepsById,
  trackedLatestItemValue,
  autoOpenChildIndex,
  autoOpenAttemptNumber,
  autoExpandPlan,
}: innerProps) => {
  const [openedItems, setOpenedItems] = useState<string[]>([]);
  const previousTrackedItemValueRef = useRef<string | null>(trackedLatestItemValue ?? null);
  const hasOpenedInitialTrackedItemRef = useRef(false);
  const appliedAutoExpandPlanRef = useRef<AutoExpandPlan | null>(null);

  useEffect(() => {
    const itemValues = new Set(items.map((item) => item.value));
    const autoOpenTarget = autoOpenChildIndex
      ? items.find(
          (item) =>
            item.childIndex === autoOpenChildIndex &&
            (item.attemptNumber === undefined ||
              autoOpenAttemptNumber == null ||
              item.attemptNumber === autoOpenAttemptNumber),
        )
      : undefined;
    setOpenedItems((currentItems) => {
      const nextItems = currentItems.filter((item) => itemValues.has(item));
      const previousTrackedItemValue = previousTrackedItemValueRef.current;

      if (
        trackedLatestItemValue &&
        previousTrackedItemValue &&
        previousTrackedItemValue !== trackedLatestItemValue
      ) {
        const remappedItems = nextItems.map((item) =>
          item === previousTrackedItemValue ? trackedLatestItemValue : item,
        );
        return areStringArraysEqual(currentItems, remappedItems) ? currentItems : remappedItems;
      }

      if (
        trackedLatestItemValue &&
        !autoOpenTarget &&
        !hasOpenedInitialTrackedItemRef.current
      ) {
        hasOpenedInitialTrackedItemRef.current = true;
        const withCurrentAttempt = currentItems.includes(trackedLatestItemValue)
          ? currentItems
          : [...currentItems, trackedLatestItemValue];
        return areStringArraysEqual(currentItems, withCurrentAttempt) ? currentItems : withCurrentAttempt;
      }

      return areStringArraysEqual(currentItems, nextItems) ? currentItems : nextItems;
    });
    previousTrackedItemValueRef.current = trackedLatestItemValue ?? null;
  }, [autoOpenAttemptNumber, autoOpenChildIndex, items, trackedLatestItemValue]);

  useEffect(() => {
    if (!autoOpenChildIndex || autoExpandPlan === appliedAutoExpandPlanRef.current) return;
    const target = items.find(
      (item) =>
        item.childIndex === autoOpenChildIndex &&
        (item.attemptNumber === undefined ||
          autoOpenAttemptNumber == null ||
          item.attemptNumber === autoOpenAttemptNumber),
    );
    if (target) {
      setOpenedItems((current) =>
        current.includes(target.value) ? current : [...current, target.value],
      );
      appliedAutoExpandPlanRef.current = autoExpandPlan ?? null;
    }
  }, [autoExpandPlan, autoOpenAttemptNumber, autoOpenChildIndex, items]);

  return (
    <Accordion multiple={true} value={openedItems} onChange={setOpenedItems}>
      {items.map((item, index) => {
        return (
          <ChildScopeAccordionItem
            key={item.value}
            value={item.value}
            label={item.label}
            status={item.status}
            run={run}
            enabled={openedItems.includes(item.value)}
            isOpen={openedItems.includes(item.value)}
            showDivider={index > 0}
            parentSteps={parentSteps}
            stepsById={stepsById}
            parentPath={item.parentPath ?? parentSteps.at(-1)?.full_path}
            childIndex={item.childIndex}
            attemptNumber={item.attemptNumber}
            scopeParentStepRunId={item.scopeParentStepRunId}
            outcomeLabel={item.outcomeLabel}
            outcomeTone={item.outcomeTone}
            detailText={item.detailText}
            lifecycleText={item.lifecycleText}
            historical={item.historical}
            autoExpandPlan={autoExpandPlan}
          />
        );
      })}
    </Accordion>
  );
};

const areStringArraysEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const getChildResultsLabel = (stepType: StepType): string => {
  if (stepType === StepType.Retry) return 'Retry attempts';
  if (stepType === StepType.LocalSubflowCall) return 'Module step results';
  return 'Children Step Results';
};

export default StepIterationsRenderer;
